package com.bulut.mecitogullari.ui.screens.projectdetail

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.unitDisplayLabel
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.core.util.formatDate
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.UnitPayment
import com.bulut.mecitogullari.data.model.UnitStatus
import com.bulut.mecitogullari.ui.components.AmountField
import com.bulut.mecitogullari.ui.components.DateField
import com.bulut.mecitogullari.ui.theme.AppColors
import kotlin.math.roundToLong

/**
 * Apartment editor. Fields adapt to the chosen status:
 *  - Satıldı: sale date + price required; recorded in the ledger as income.
 *  - Rezerve: buyer info only — no date, no ledger entry.
 *  - Satılık: just an optional asking price and notes.
 */
@Composable
fun UnitDetailDialog(
    unit: ApartmentUnit,
    onSave: (ApartmentUnit) -> Unit,
    onDismiss: () -> Unit,
    barterCraftsmanName: String? = null,
) {
    val strings = LocalStrings.current
    // Bartered units are managed from the craftsman page; here we only
    // show the deal and allow a note.
    if (unit.barterCraftsmanId != null) {
        BarterUnitDialog(
            unit = unit,
            craftsmanName = barterCraftsmanName,
            onSaveNotes = { notes -> onSave(unit.copy(notes = notes)) },
            onDismiss = onDismiss,
        )
        return
    }

    var status by remember { mutableStateOf(unit.status) }
    var priceText by remember {
        mutableStateOf(unit.salePrice?.roundToLong()?.toString() ?: "")
    }
    var saleDate by remember { mutableStateOf(unit.saleDateEpochMillis) }
    var buyerName by remember { mutableStateOf(unit.buyerName) }
    var buyerPhone by remember { mutableStateOf(unit.buyerPhone) }
    var notes by remember { mutableStateOf(unit.notes) }
    var error by remember { mutableStateOf<String?>(null) }

    // Installments actually received; nothing is added automatically —
    // the user records each payment explicitly.
    val paymentDrafts = remember {
        mutableStateListOf(
            *unit.payments
                .map { PaymentDraft(it.id, it.dateEpochMillis, it.amount.roundToLong().toString()) }
                .toTypedArray()
        )
    }

    val statusColor = when (status) {
        UnitStatus.SOLD -> AppColors.Income
        UnitStatus.RESERVED -> AppColors.Reserved
        UnitStatus.AVAILABLE -> AppColors.TextSecondary
    }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier
                    .verticalScroll(rememberScrollState())
                    .padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                // Header: colored badge + title
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(44.dp)
                            .background(statusColor.copy(alpha = 0.15f), CircleShape),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = unit.label,
                            color = statusColor,
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                        )
                    }
                    Column(modifier = Modifier.padding(start = 12.dp)) {
                        Text(
                            text = unitDisplayLabel(unit.block, unit.number),
                            style = MaterialTheme.typography.titleMedium,
                        )
                        Text(
                            text = strings.floorShort.fillTemplate("floor" to unit.floor.toString()),
                            style = MaterialTheme.typography.bodySmall,
                            color = AppColors.TextSecondary,
                        )
                    }
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp))
                        .padding(4.dp),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    StatusOption(strings.statusAvailable, status == UnitStatus.AVAILABLE, AppColors.TextSecondary, Modifier.weight(1f)) {
                        status = UnitStatus.AVAILABLE
                        error = null
                    }
                    StatusOption(strings.statusReserved, status == UnitStatus.RESERVED, AppColors.Reserved, Modifier.weight(1f)) {
                        status = UnitStatus.RESERVED
                        error = null
                    }
                    StatusOption(strings.statusSold, status == UnitStatus.SOLD, AppColors.Income, Modifier.weight(1f)) {
                        status = UnitStatus.SOLD
                        if (saleDate == null) saleDate = todayUtcMidnightMillis()
                        // A fresh sale starts with a clean buyer — don't
                        // carry over the previous owner's info.
                        if (unit.status != UnitStatus.SOLD) {
                            buyerName = ""
                            buyerPhone = ""
                        }
                        error = null
                    }
                }

                if (status == UnitStatus.SOLD) {
                    DateField(
                        label = strings.saleDateLabel,
                        dateEpochMillis = saleDate,
                        onDateSelected = { saleDate = it },
                        modifier = Modifier.fillMaxWidth(),
                    )
                }

                AmountField(
                    label = if (status == UnitStatus.SOLD) strings.salePriceLabel else "Fiyat (₺, opsiyonel)",
                    value = priceText,
                    onValueChange = {
                        priceText = it
                        error = null
                    },
                    modifier = Modifier.fillMaxWidth(),
                )

                if (status == UnitStatus.SOLD) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = strings.paymentsReceived,
                            style = MaterialTheme.typography.labelLarge,
                            color = AppColors.TextSecondary,
                        )
                        TextButton(
                            onClick = {
                                paymentDrafts += PaymentDraft(
                                    id = newId(),
                                    dateEpochMillis = todayUtcMidnightMillis(),
                                    amountText = "",
                                )
                            },
                        ) {
                            Text(strings.addPayment)
                        }
                    }

                    paymentDrafts.forEachIndexed { index, draft ->
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp))
                                .padding(10.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                DateField(
                                    label = "Tarih",
                                    dateEpochMillis = draft.dateEpochMillis,
                                    onDateSelected = { paymentDrafts[index] = draft.copy(dateEpochMillis = it) },
                                    modifier = Modifier.weight(1f),
                                )
                                IconButton(onClick = { paymentDrafts.removeAt(index) }) {
                                    Icon(
                                        Icons.Default.Close,
                                        contentDescription = strings.deletePayment,
                                        tint = AppColors.TextSecondary,
                                    )
                                }
                            }
                            AmountField(
                                label = "Tutar (₺)",
                                value = draft.amountText,
                                onValueChange = {
                                    paymentDrafts[index] = draft.copy(amountText = it)
                                    error = null
                                },
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }
                    }

                    val price = priceText.toLongOrNull()?.toDouble() ?: 0.0
                    val paid = paymentDrafts.sumOf { it.amount }
                    val remaining = (price - paid).coerceAtLeast(0.0)
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp))
                            .padding(12.dp),
                    ) {
                        SummaryLine(strings.pdfPaid, formatAmount(paid), AppColors.Income)
                        if (remaining > 0) {
                            SummaryLine(strings.remainingDebt, formatAmount(remaining), AppColors.Expense)
                        } else if (price > 0) {
                            SummaryLine(strings.remainingDebt, strings.fullyPaid, AppColors.Income)
                        }
                    }
                }

                if (status != UnitStatus.AVAILABLE) {
                    OutlinedTextField(
                        value = buyerName,
                        onValueChange = { buyerName = it },
                        label = { Text(strings.buyerNameLabel) },
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth(),
                    )
                    OutlinedTextField(
                        value = buyerPhone,
                        onValueChange = { buyerPhone = it },
                        label = { Text(strings.buyerPhoneLabel) },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.fillMaxWidth(),
                    )
                }

                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text(strings.noteOptional) },
                    minLines = 2,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )

                if (status == UnitStatus.SOLD) {
                    Text(
                        text = strings.saleMarksUnit,
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.Income,
                    )
                }

                error?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.Expense,
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(
                        onClick = onDismiss,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(strings.cancel, color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = {
                            val price = priceText.toLongOrNull()?.toDouble()
                            if (status == UnitStatus.SOLD && (price == null || price <= 0.0)) {
                                error = strings.errorSalePrice
                                return@Button
                            }
                            onSave(
                                unit.copy(
                                    status = status,
                                    salePrice = price,
                                    saleDateEpochMillis = if (status == UnitStatus.SOLD) saleDate else null,
                                    buyerName = if (status == UnitStatus.AVAILABLE) "" else buyerName.trim(),
                                    buyerPhone = if (status == UnitStatus.AVAILABLE) "" else buyerPhone.trim(),
                                    notes = notes.trim(),
                                    payments = if (status == UnitStatus.SOLD) {
                                        paymentDrafts
                                            .filter { it.amount > 0 }
                                            .map { UnitPayment(it.id, it.dateEpochMillis, it.amount) }
                                    } else {
                                        emptyList()
                                    },
                                )
                            )
                        },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.save, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

/** Read-mostly dialog for a unit given as barter (iş karşılığı). */
@Composable
private fun BarterUnitDialog(
    unit: ApartmentUnit,
    craftsmanName: String?,
    onSaveNotes: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    val strings = LocalStrings.current
    var notes by remember { mutableStateOf(unit.notes) }

    Dialog(onDismissRequest = onDismiss) {
        Card(
            shape = RoundedCornerShape(24.dp),
            colors = CardDefaults.cardColors(containerColor = Color.White),
        ) {
            Column(
                modifier = Modifier
                    .verticalScroll(rememberScrollState())
                    .padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .size(44.dp)
                            .background(AppColors.Reserved.copy(alpha = 0.16f), CircleShape),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = unit.label,
                            color = AppColors.Reserved,
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                        )
                    }
                    Column(modifier = Modifier.padding(start = 12.dp)) {
                        Text(
                            text = unitDisplayLabel(unit.block, unit.number),
                            style = MaterialTheme.typography.titleMedium,
                        )
                        Text(
                            text = strings.barterUnitSubtitle.fillTemplate("floor" to unit.floor.toString()),
                            style = MaterialTheme.typography.bodySmall,
                            color = AppColors.Reserved,
                        )
                    }
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(AppColors.SurfaceMuted, RoundedCornerShape(12.dp))
                        .padding(12.dp),
                ) {
                    Text(
                        text = strings.barterInfo.fillTemplate("unit" to unitDisplayLabel(unit.block, unit.number)),
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Medium,
                    )
                    unit.saleDateEpochMillis?.let {
                        InfoRow(strings.saleDateLabel, formatDate(it))
                    }
                    InfoRow("Daire Bedeli", formatAmount(unit.salePrice ?: 0.0))
                    Text(
                        text = strings.craftsmenSubtitle,
                        style = MaterialTheme.typography.bodySmall,
                        color = AppColors.TextSecondary,
                        modifier = Modifier.padding(top = 8.dp),
                    )
                }

                OutlinedTextField(
                    value = notes,
                    onValueChange = { notes = it },
                    label = { Text(strings.noteOptional) },
                    minLines = 2,
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth(),
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    TextButton(onClick = onDismiss, modifier = Modifier.weight(1f)) {
                        Text("Kapat", color = AppColors.TextSecondary)
                    }
                    Button(
                        onClick = { onSaveNotes(notes.trim()) },
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                        modifier = Modifier.weight(1f).height(44.dp),
                    ) {
                        Text(strings.save, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
    }
}

@Composable
private fun InfoRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(top = 6.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(label, style = MaterialTheme.typography.bodySmall, color = AppColors.TextSecondary)
        Text(value, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Medium)
    }
}

/** Editable installment row inside the dialog. */
data class PaymentDraft(
    val id: String,
    val dateEpochMillis: Long,
    val amountText: String,
) {
    val amount: Double get() = amountText.toLongOrNull()?.toDouble() ?: 0.0
}

@Composable
private fun SummaryLine(label: String, value: String, valueColor: Color) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(label, style = MaterialTheme.typography.bodyMedium, color = AppColors.TextSecondary)
        Text(value, style = MaterialTheme.typography.bodyMedium, color = valueColor, fontWeight = FontWeight.SemiBold)
    }
}

@Composable
private fun StatusOption(
    text: String,
    isSelected: Boolean,
    activeColor: Color,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    Box(
        modifier = modifier
            .background(
                color = if (isSelected) activeColor else Color.Transparent,
                shape = RoundedCornerShape(9.dp),
            )
            .clickable(onClick = onClick)
            .padding(vertical = 10.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = text,
            color = if (isSelected) Color.White else AppColors.TextSecondary,
            style = MaterialTheme.typography.labelLarge,
        )
    }
}
